import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { opportunityService } from '../services/opportunityService';
import DeactivateModal from './DeactivateModal';
import UnpublishModal from './UnpublishModal';
import RepublishModal from './RepublishModal';
import ResendAppliesModal from './ResendAppliesModal';

interface OpportunityDetailsModalProps {
  opportunityId: number;
  onClose: () => void;
  mode?: 'modal' | 'page';
}

interface OpportunityDetails {
  id: number;
  name: string;
  fancy_name?: string;
  description?: string;
  prerequisite?: string;
  desirable?: string;
  perks?: string;
  other_info?: string;
  level?: string;
  regime: number;
  quantity?: number;
  salary_from?: number;
  salary_to?: number;
  show_salary?: boolean;
  type_id?: number;
  category_id?: number;
  workplace: number;
  apply_method: number;
  apply_link?: string;
  apply_email?: string;
  apply_email_subject?: string;
  require_cv?: boolean;
  require_portfolio?: boolean;
  highlight?: boolean;
  confidential?: boolean;
  published: boolean;
  status: number;
  filter_state?: boolean;
  filter_salary?: boolean;
  deny_duplicated_candidates?: boolean;
  hide_ads?: boolean;
  search_hidden?: boolean;
  moderated?: boolean;
  pro?: boolean;
  jooble_paid?: boolean;
  facebook_conversion_code_candidates?: string;
  tags?: string;
  comments?: string;
  reply_email?: boolean;
  reply_email_subject?: string;
  reply_email_body?: string;
  use_custom_template?: boolean;
  custom_template?: string;
  applies_count: number;
  created_at: string;
  published_at?: string;
  activated_at?: string;
  expires_at?: string;
  poderoso_expires_at?: string;
  url?: string;
  recruiter?: {
    id: number;
    name?: string;
    email: string;
  };
  type_name?: string;
  category_name?: string;
  addresses?: Array<{
    id: number;
    street?: string;
    number?: string;
    complement?: string;
    zip?: string;
    neighborhood?: string;
    city: string;
    state: string;
    country: string;
    telephone?: string;
  }>;
  questions?: Array<{
    id: number;
    body: string;
    required?: boolean;
    question_type?: string;
    filter?: boolean;
    opportunity_answers?: Array<{
      id: number;
      body: string;
      correct: boolean;
    }>;
  }>;
  recruiters?: Array<{
    id: number;
    recruiter_id?: number;
    name?: string;
    email: string;
    poderoso: boolean;
  }>;
}

const OpportunityDetailsModal: React.FC<OpportunityDetailsModalProps> = ({ opportunityId, onClose, mode = 'modal' }) => {
  const [opportunity, setOpportunity] = useState<OpportunityDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showRepublishModal, setShowRepublishModal] = useState(false);
  const [unpublishInfo, setUnpublishInfo] = useState<any>(null);
  const [deactivationInfo, setDeactivationInfo] = useState<any>(null);
  const [resendAppliesMode, setResendAppliesMode] = useState<'all' | 'first_n'>('all');
  const [resendAppliesLimit, setResendAppliesLimit] = useState<number>(1);
  const [resendingApplies, setResendingApplies] = useState(false);
  const [showResendAppliesModal, setShowResendAppliesModal] = useState(false);

  useEffect(() => {
    fetchOpportunityDetails();
  }, [opportunityId]);

  const fetchOpportunityDetails = async () => {
    try {
      setLoading(true);
      const [oppResponse, addressResponse, questionsResponse, recruitersResponse] = await Promise.all([
        api.get(`/opportunities/${opportunityId}`),
        api.get(`/opportunities/${opportunityId}/addresses`).catch(() => ({ data: [] })),
        api.get(`/opportunities/${opportunityId}/questions`).catch(() => ({ data: [] })),
        api.get(`/opportunities/${opportunityId}/recruiters`).catch(() => ({ data: [] }))
      ]);

      const opportunityData = {
        ...oppResponse.data,
        addresses: addressResponse.data,
        questions: questionsResponse.data,
        recruiters: recruitersResponse.data
      };

      setOpportunity(opportunityData);

      if (!opportunityData.published && opportunityData.status === 2) {
        try {
          const unpublishResponse = await opportunityService.getUnpublishInfo(opportunityId);
          setUnpublishInfo(unpublishResponse);
        } catch (error) {
          setUnpublishInfo(null);
        }
      } else {
        setUnpublishInfo(null);
      }

      if (opportunityData.status === 3) {
        try {
          const deactivationResponse = await opportunityService.getDeactivationInfo(opportunityId);
          setDeactivationInfo(deactivationResponse);
        } catch (error) {
          setDeactivationInfo(null);
        }
      } else {
        setDeactivationInfo(null);
      }
    } catch (error: any) {
      console.error('Error fetching opportunity details:', error);
      toast.error('Erro ao carregar detalhes da oportunidade');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: number) => {
    const statusMap: { [key: number]: string } = {
      0: 'Rascunho',
      1: 'Não Paga',
      2: 'Ativa',
      3: 'Desativada'
    };
    return statusMap[status] || 'Desconhecido';
  };

  const getStatusColor = (status: number) => {
    const colorMap: { [key: number]: string } = {
      0: colors.textLight,
      1: '#FFA500',
      2: colors.success,
      3: colors.error
    };
    return colorMap[status] || colors.textLight;
  };

  const getWorkplaceLabel = (workplace: number) => {
    const workplaceMap: { [key: number]: string } = {
      1: 'Home Office',
      2: 'Na empresa',
      3: 'Outro lugar',
      4: 'Híbrido'
    };
    return workplaceMap[workplace] || 'Não informado';
  };

  const getRegimeLabel = (regime: number) => {
    const regimeMap: { [key: number]: string } = {
      1: 'CLT',
      2: 'PJ',
      3: 'Indiferente',
      4: 'Estágio'
    };
    return regimeMap[regime] || 'Não informado';
  };

  const getApplyMethodLabel = (method: number) => {
    const methodMap: { [key: number]: string } = {
      1: 'Link Externo',
      2: 'Email',
      3: 'Caesar'
    };
    return methodMap[method] || 'Não informado';
  };

  const getLevelLabel = (level?: string) => {
    const levelMap: { [key: string]: string } = {
      'junior': 'Júnior',
      'mid-level': 'Pleno',
      'senior': 'Senior'
    };
    return level ? levelMap[level] || level : 'Não informado';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };


  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getUnpublishRecruiters = () => {
    if (!opportunity) return [];

    const recruitersMap = new Map<number, { id: number; recruiter_id: number; name: string; email: string }>();

    if (opportunity.recruiter?.id) {
      recruitersMap.set(opportunity.recruiter.id, {
        id: opportunity.recruiter.id,
        recruiter_id: opportunity.recruiter.id,
        name: opportunity.recruiter.name || opportunity.recruiter.email,
        email: opportunity.recruiter.email,
      });
    }

    opportunity.recruiters?.forEach((recruiter) => {
      const recruiterId = recruiter.recruiter_id || recruiter.id;
      if (!recruiterId) return;
      recruitersMap.set(recruiterId, {
        id: recruiter.id,
        recruiter_id: recruiterId,
        name: recruiter.name || recruiter.email,
        email: recruiter.email,
      });
    });

    return Array.from(recruitersMap.values());
  };

  const refreshDetails = async () => {
    await fetchOpportunityDetails();
  };

  const handleRepublish = async () => {
    try {
      await opportunityService.republish(opportunityId);
      toast.success('Vaga republicada com sucesso!');
      await refreshDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao republicar vaga');
    }
  };

  const handleDeactivate = async () => {
    try {
      await opportunityService.deactivate(opportunityId);
      toast.success('Vaga desativada com sucesso!');
      await refreshDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao desativar vaga');
    }
  };

  const handleUnpublish = async (reason: string, recruiterId: number, createAfterSales: boolean) => {
    try {
      await opportunityService.unpublish(opportunityId, reason, recruiterId, !createAfterSales);
      toast.success('Vaga despublicada com sucesso!');
      await refreshDetails();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao despublicar vaga');
    }
  };

  const handleResendApplies = async () => {
    if (!opportunity) return;

    const limit = resendAppliesMode === 'first_n'
      ? Math.min(Math.max(Number(resendAppliesLimit) || 1, 1), opportunity.applies_count)
      : undefined;

    try {
      setResendingApplies(true);
      const result = await opportunityService.resendApplies(opportunityId, resendAppliesMode, limit);
      toast.success(result.message || 'Candidaturas reenviadas com sucesso!');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao reenviar candidaturas');
    } finally {
      setResendingApplies(false);
    }
  };

  const formatCurrency = (value?: number) => value ? `R$ ${Number(value).toLocaleString('pt-BR')}` : '-';

  const getDisplayName = () => opportunity?.fancy_name || opportunity?.name || 'Sem título';

  const renderPreviewMeta = (label: string, value?: string | number | null) => (
    <div style={styles.previewMetaItem}>
      <strong>{label}</strong>
      <span>{value || '-'}</span>
    </div>
  );

  const renderPreviewSection = (title: string, value?: string | null) => (
    value ? (
      <div style={styles.previewSection}>
        <h4 style={styles.previewSectionTitle}>{title}</h4>
        <div style={styles.previewText}>{value}</div>
      </div>
    ) : null
  );

  const renderPreview = () => {
    if (!opportunity) return null;

    const salary = opportunity.show_salary
      ? [formatCurrency(opportunity.salary_from), formatCurrency(opportunity.salary_to)].filter(value => value !== '-').join(' até ') || '-'
      : 'Não exibido';

    const firstAddress = opportunity.addresses?.[0];

    return (
      <div style={styles.previewCard}>
        <div style={styles.previewHeader}>
          <span style={styles.previewEyebrow}>Preview da vaga</span>
          <h3 style={styles.previewTitle}>{getDisplayName()}</h3>
          <div style={styles.previewBadges}>
            <span style={{ ...styles.previewBadge, backgroundColor: getStatusColor(opportunity.status) }}>
              {getStatusLabel(opportunity.status)}
            </span>
            <span style={{ ...styles.previewBadge, backgroundColor: opportunity.published ? colors.success : colors.error }}>
              {opportunity.published ? 'Publicada' : 'Não publicada'}
            </span>
            {opportunity.moderated !== undefined && (
              <span style={{ ...styles.previewBadge, backgroundColor: opportunity.moderated ? colors.success : '#F59E0B' }}>
                {opportunity.moderated ? 'Moderada' : 'Em moderação'}
              </span>
            )}
          </div>
        </div>

        <div style={styles.previewMetaGrid}>
          {renderPreviewMeta('Tipo', opportunity.type_name)}
          {renderPreviewMeta('Categoria', opportunity.category_name)}
          {renderPreviewMeta('Nível', getLevelLabel(opportunity.level))}
          {renderPreviewMeta('Regime', getRegimeLabel(opportunity.regime))}
          {renderPreviewMeta('Local', getWorkplaceLabel(opportunity.workplace))}
          {renderPreviewMeta('Quantidade', opportunity.quantity)}
          {renderPreviewMeta('Salário', salary)}
          {renderPreviewMeta('Candidaturas', opportunity.applies_count)}
        </div>

        {renderPreviewSection('Descrição', opportunity.description)}
        {renderPreviewSection('Requisitos', opportunity.prerequisite)}
        {renderPreviewSection('Desejável', opportunity.desirable)}
        {renderPreviewSection('Benefícios', opportunity.perks)}
        {renderPreviewSection('Outras informações', opportunity.other_info)}

        {firstAddress && (
          <div style={styles.previewSection}>
            <h4 style={styles.previewSectionTitle}>Endereço</h4>
            <div style={styles.previewText}>
              {[firstAddress.street, firstAddress.number, firstAddress.neighborhood, firstAddress.city, firstAddress.state, firstAddress.country].filter(Boolean).join(', ')}
            </div>
          </div>
        )}

        <div style={styles.previewFooter}>
          <strong>Recrutador</strong>
          <span>{opportunity.recruiter?.name || opportunity.recruiter?.email || '-'}</span>
          {opportunity.url && (
            <a href={opportunity.url} target="_blank" rel="noopener noreferrer" style={styles.previewLink}>
              Abrir vaga publicada
            </a>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={mode === 'page' ? styles.pageContainer : styles.overlay}>
        <div style={mode === 'page' ? styles.pageShell : styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>Carregando...</h2>
            {mode === 'modal' && <button onClick={onClose} style={styles.closeBtn}>×</button>}
          </div>
          <div style={styles.loading}>Carregando detalhes da oportunidade...</div>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div style={mode === 'page' ? styles.pageContainer : styles.overlay}>
        <div style={mode === 'page' ? styles.pageShell : styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>Erro</h2>
            {mode === 'modal' && <button onClick={onClose} style={styles.closeBtn}>×</button>}
          </div>
          <div style={styles.error}>Oportunidade não encontrada</div>
        </div>
      </div>
    );
  }

  return (
    <div style={mode === 'page' ? styles.pageContainer : styles.overlay}>
      <div style={mode === 'page' ? styles.pageShell : styles.modal}>
        <div style={styles.header}>
          <div>
            <button type="button" onClick={onClose} style={styles.backBtn}>
              ← Voltar
            </button>
            <h2 style={styles.title}>Detalhes da Oportunidade #{opportunity.id}</h2>
          </div>
          {mode === 'modal' && <button onClick={onClose} style={styles.closeBtn}>×</button>}
        </div>
        
        <div style={styles.content}>
          <div style={styles.detailsLayout}>
            <div style={styles.detailsColumn}>
          {/* Informações Básicas */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Informações Básicas</h3>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>ID:</label>
                <span style={styles.value}>{opportunity.id}</span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Nome:</label>
                <span style={styles.value}>{opportunity.name}</span>
              </div>
              {opportunity.fancy_name && (
                <div style={styles.field}>
                  <label style={styles.label}>Nome Fantasia:</label>
                  <span style={styles.value}>{opportunity.fancy_name}</span>
                </div>
              )}
              {opportunity.tags && (
                <div style={styles.field}>
                  <label style={styles.label}>Tags:</label>
                  <span style={styles.value}>{opportunity.tags}</span>
                </div>
              )}
              <div style={styles.field}>
                <label style={styles.label}>Status:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: getStatusColor(opportunity.status)
                }}>
                  {getStatusLabel(opportunity.status)}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Publicada:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.published ? colors.success : colors.error
                }}>
                  {opportunity.published ? 'SIM' : 'NÃO'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Candidaturas:</label>
                <span style={styles.value}>{opportunity.applies_count}</span>
              </div>
              {opportunity.type_name && (
                <div style={styles.field}>
                  <label style={styles.label}>Tipo:</label>
                  <span style={styles.value}>{opportunity.type_name}</span>
                </div>
              )}
              {opportunity.category_name && (
                <div style={styles.field}>
                  <label style={styles.label}>Categoria:</label>
                  <span style={styles.value}>{opportunity.category_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* URL */}
          {opportunity.url && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>URL</h3>
              <div style={styles.urlContainer}>
                <a
                  href={opportunity.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.urlLink}
                >
                  {opportunity.url}
                </a>
                <button
                  onClick={() => copyToClipboard(opportunity.url!)}
                  style={styles.copyBtn}
                >
                  Copiar
                </button>
              </div>
            </div>
          )}

          {/* Recrutadores */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Recrutadores</h3>
            <div style={styles.recruitersList}>
              {/* Recrutador Principal */}
              {opportunity.recruiter && (
                <div style={styles.recruiterItem}>
                  <div style={styles.recruiterInfo}>
                    <span style={styles.recruiterName}>
                      {opportunity.recruiter.name || opportunity.recruiter.email}
                    </span>
                    <span style={styles.recruiterEmail}>{opportunity.recruiter.email}</span>
                  </div>
                  <span style={styles.ownerTag}>PRINCIPAL</span>
                </div>
              )}
              
              {/* Outros Recrutadores */}
              {opportunity.recruiters && opportunity.recruiters.length > 0 && opportunity.recruiters.map((recruiter) => (
                <div key={recruiter.id} style={styles.recruiterItem}>
                  <div style={styles.recruiterInfo}>
                    <span style={styles.recruiterName}>
                      {recruiter.name || recruiter.email}
                    </span>
                    <span style={styles.recruiterEmail}>{recruiter.email}</span>
                  </div>
                  {recruiter.poderoso && (
                    <span style={styles.poderosoTag}>PODEROSO</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Salário */}
          {(opportunity.salary_from || opportunity.salary_to) && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Salário</h3>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>Mostrar Salário:</label>
                  <span style={{
                    ...styles.badge,
                    backgroundColor: opportunity.show_salary ? colors.success : colors.error
                  }}>
                    {opportunity.show_salary ? 'SIM' : 'NÃO'}
                  </span>
                </div>
                {opportunity.salary_from && (
                  <div style={styles.field}>
                    <label style={styles.label}>De:</label>
                    <span style={styles.value}>R$ {opportunity.salary_from.toLocaleString()}</span>
                  </div>
                )}
                {opportunity.salary_to && (
                  <div style={styles.field}>
                    <label style={styles.label}>Até:</label>
                    <span style={styles.value}>R$ {opportunity.salary_to.toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Detalhes do Trabalho */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Detalhes do Trabalho</h3>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Local de Trabalho:</label>
                <span style={styles.value}>{getWorkplaceLabel(opportunity.workplace)}</span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Regime:</label>
                <span style={styles.value}>{getRegimeLabel(opportunity.regime)}</span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Nível:</label>
                <span style={styles.value}>{getLevelLabel(opportunity.level)}</span>
              </div>
              {opportunity.quantity && (
                <div style={styles.field}>
                  <label style={styles.label}>Quantidade:</label>
                  <span style={styles.value}>{opportunity.quantity}</span>
                </div>
              )}
            </div>
          </div>

          {/* Endereços */}
          {opportunity.addresses && opportunity.addresses.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Endereços</h3>
              <div style={styles.addressList}>
                {opportunity.addresses.map((address) => (
                  <div key={address.id} style={styles.addressItem}>
                    {address.city}, {address.state} - {address.country}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Perguntas */}
          {opportunity.questions && opportunity.questions.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Questões para Candidatos</h3>
              <div style={styles.questionsList}>
                {opportunity.questions.map((question, index) => (
                  <div key={question.id} style={styles.questionItem}>
                    <div style={styles.questionHeader}>
                      <strong>{index + 1}. {question.body}</strong>
                      <div style={styles.questionMeta}>
                        {question.required && (
                          <span style={styles.requiredBadge}>OBRIGATÓRIA</span>
                        )}
                        {question.filter && (
                          <span style={styles.filterBadge}>FILTRO</span>
                        )}
                        {question.question_type && (
                          <span style={styles.typeBadge}>
                            {question.question_type === 'text' ? 'TEXTO' : 
                             question.question_type === 'multiple_choice' ? 'Múltipla Escolha' :
                             question.question_type === 'boolean' ? 'Múltipla Escolha' : 
                             question.question_type.toUpperCase()}
                          </span>
                        )}
                      </div>
                    </div>
                    {question.opportunity_answers && question.opportunity_answers.length > 0 && (
                      <div style={styles.answersSection}>
                        <strong>Respostas:</strong>
                        <ul style={styles.answersList}>
                          {question.opportunity_answers.map((answer) => (
                            <li key={answer.id} style={styles.answerItem}>
                              {answer.body}
                              {answer.correct && (
                                <span style={styles.correctBadge}>CORRETA</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Descrições */}
          {opportunity.description && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Descrição</h3>
              <div style={styles.textContent}>{opportunity.description}</div>
            </div>
          )}

          {opportunity.prerequisite && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Requisitos</h3>
              <div style={styles.textContent}>{opportunity.prerequisite}</div>
            </div>
          )}

          {opportunity.desirable && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Desejável</h3>
              <div style={styles.textContent}>{opportunity.desirable}</div>
            </div>
          )}

          {opportunity.perks && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Benefícios</h3>
              <div style={styles.textContent}>{opportunity.perks}</div>
            </div>
          )}

          {opportunity.other_info && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Outras Informações</h3>
              <div style={styles.textContent}>{opportunity.other_info}</div>
            </div>
          )}

          {/* Candidatura */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Método de Candidatura</h3>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Método:</label>
                <span style={styles.value}>{getApplyMethodLabel(opportunity.apply_method)}</span>
              </div>
              {opportunity.apply_link && (
                <div style={styles.field}>
                  <label style={styles.label}>Link:</label>
                  <a href={opportunity.apply_link} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    {opportunity.apply_link}
                  </a>
                </div>
              )}
              {opportunity.apply_email && (
                <div style={styles.field}>
                  <label style={styles.label}>Email:</label>
                  <span style={styles.value}>{opportunity.apply_email}</span>
                </div>
              )}
              {opportunity.apply_email_subject && (
                <div style={styles.field}>
                  <label style={styles.label}>Assunto do Email:</label>
                  <span style={styles.value}>{opportunity.apply_email_subject}</span>
                </div>
              )}
              <div style={styles.field}>
                <label style={styles.label}>CV Obrigatório:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.require_cv ? colors.success : colors.error
                }}>
                  {opportunity.require_cv ? 'SIM' : 'NÃO'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Portfólio Obrigatório:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.require_portfolio ? colors.success : colors.error
                }}>
                  {opportunity.require_portfolio ? 'SIM' : 'NÃO'}
                </span>
              </div>
            </div>
            {opportunity.comments && (
              <div style={styles.textContent}>
                <strong>Comentários:</strong><br/>
                {opportunity.comments}
              </div>
            )}
          </div>

          {/* Configurações */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Configurações</h3>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Destaque:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.highlight ? colors.success : colors.error
                }}>
                  {opportunity.highlight ? 'SIM' : 'NÃO'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Confidencial:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.confidential ? colors.success : colors.error
                }}>
                  {opportunity.confidential ? 'SIM' : 'NÃO'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Esconder Publicidade:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.hide_ads ? colors.success : colors.error
                }}>
                  {opportunity.hide_ads ? 'SIM' : 'NÃO'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Esconder na Busca:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.search_hidden ? colors.success : colors.error
                }}>
                  {opportunity.search_hidden ? 'SIM' : 'NÃO'}
                </span>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Moderado:</label>
                <span style={{
                  ...styles.badge,
                  backgroundColor: opportunity.moderated ? colors.success : colors.error
                }}>
                  {opportunity.moderated ? 'SIM' : 'NÃO'}
                </span>
              </div>
            </div>
          </div>

          {/* Ações */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Ações da Vaga</h3>

            {opportunity.applies_count > 0 ? (
              <div style={styles.resendAppliesCard}>
                <div>
                  <div style={styles.resendAppliesTitle}>📨 Reenviar candidaturas</div>
                  <p style={styles.actionDescription}>
                    Reenvia ao recrutador as candidaturas aprovadas pelos filtros da vaga.
                  </p>
                </div>

                <div style={styles.resendAppliesControls}>
                  <select
                    value={resendAppliesMode}
                    onChange={(event) => setResendAppliesMode(event.target.value as 'all' | 'first_n')}
                    style={styles.resendSelect}
                    disabled={resendingApplies}
                  >
                    <option value="all">Todas as candidaturas</option>
                    <option value="first_n">Somente as primeiras N</option>
                  </select>

                  {resendAppliesMode === 'first_n' && (
                    <input
                      type="number"
                      min={1}
                      max={opportunity.applies_count}
                      value={resendAppliesLimit}
                      onChange={(event) => setResendAppliesLimit(Number(event.target.value))}
                      style={styles.resendNumberInput}
                      disabled={resendingApplies}
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => setShowResendAppliesModal(true)}
                    disabled={resendingApplies}
                    style={{
                      ...styles.actionButton,
                      ...styles.resendButton,
                      opacity: resendingApplies ? 0.7 : 1,
                      cursor: resendingApplies ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {resendingApplies ? 'Reenviando...' : 'Reenviar'}
                  </button>
                </div>
              </div>
            ) : null}

            {opportunity.status === 3 ? (
              <div style={styles.statusCard}>
                <div style={styles.statusEmoji}>🚫</div>
                <h4 style={styles.statusTitle}>Vaga Desativada</h4>
                <div style={styles.statusInfo}>
                  {deactivationInfo && (
                    <>
                      <p><strong>Desativada em:</strong> {formatDate(deactivationInfo.deactivated_at)}</p>
                      {deactivationInfo.admin_email && (
                        <p><strong>Desativada por:</strong> {deactivationInfo.admin_email}</p>
                      )}
                    </>
                  )}
                  <p style={styles.statusDescription}>
                    Esta vaga foi desativada e não está mais disponível no site, Caesar ou relatórios.
                  </p>
                </div>
              </div>
            ) : null}

            {opportunity.status === 2 && !opportunity.published ? (
              <div style={styles.actionsGrid}>
                <div style={styles.statusCard}>
                  <div style={styles.statusEmoji}>📤</div>
                  <h4 style={styles.statusTitle}>Vaga Despublicada</h4>
                  <div style={styles.statusInfo}>
                    {unpublishInfo ? (
                      <>
                        <p><strong>Motivo:</strong> {unpublishInfo.reason}</p>
                        <p><strong>Despublicada em:</strong> {formatDate(unpublishInfo.created_at)}</p>
                        {unpublishInfo.recruiter_name && (
                          <p><strong>Recrutador:</strong> {unpublishInfo.recruiter_name}</p>
                        )}
                        {unpublishInfo.admin_email && (
                          <p><strong>Admin:</strong> {unpublishInfo.admin_email}</p>
                        )}
                      </>
                    ) : (
                      <p>Esta vaga foi despublicada.</p>
                    )}
                  </div>
                </div>

                <div style={styles.actionCard}>
                  <div style={styles.actionEmoji}>🚫</div>
                  <button
                    type="button"
                    onClick={() => setShowDeactivateModal(true)}
                    style={{ ...styles.actionButton, backgroundColor: colors.error }}
                  >
                    DESATIVAR A VAGA
                  </button>
                  <p style={styles.actionDescription}>
                    Só faça isso se você tiver muita certeza do que está fazendo.
                  </p>
                </div>
              </div>
            ) : null}

            {opportunity.status === 2 && opportunity.published ? (
              <div style={styles.actionsGrid}>
                <div style={styles.actionCard}>
                  <div style={styles.actionEmoji}>🔄</div>
                  <button
                    type="button"
                    onClick={() => setShowRepublishModal(true)}
                    style={styles.actionButton}
                  >
                    Republicar
                  </button>
                  <p style={styles.actionDescription}>Devolve a vaga para o topo dos resultados das buscas.</p>
                </div>

                <div style={styles.actionCard}>
                  <div style={styles.actionEmoji}>📤</div>
                  <button
                    type="button"
                    onClick={() => setShowUnpublishModal(true)}
                    style={{ ...styles.actionButton, backgroundColor: '#F59E0B' }}
                  >
                    Despublicar
                  </button>
                  <p style={styles.actionDescription}>Tira a vaga do ar e registra o motivo da despublicação.</p>
                </div>

                <div style={styles.actionCard}>
                  <div style={styles.actionEmoji}>🚫</div>
                  <button
                    type="button"
                    onClick={() => setShowDeactivateModal(true)}
                    style={{ ...styles.actionButton, backgroundColor: colors.error }}
                  >
                    DESATIVAR A VAGA
                  </button>
                  <p style={styles.actionDescription}>
                    A vaga sairá do site, Caesar e relatórios.
                  </p>
                </div>
              </div>
            ) : null}

            {(opportunity.status !== 2 && opportunity.status !== 3) ? (
              <div style={styles.statusCard}>
                <div style={styles.statusEmoji}>ℹ️</div>
                <h4 style={styles.statusTitle}>Nenhuma ação disponível</h4>
                <p style={styles.statusDescription}>
                  As ações só estão disponíveis para vagas ativas.
                </p>
              </div>
            ) : null}
          </div>

          {/* Datas */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Datas</h3>
            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Criada em:</label>
                <span style={styles.value}>
                  {new Date(opportunity.created_at).toLocaleString('pt-BR')}
                </span>
              </div>
              {opportunity.published_at && (
                <div style={styles.field}>
                  <label style={styles.label}>Publicada em:</label>
                  <span style={styles.value}>
                    {new Date(opportunity.published_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
              {opportunity.activated_at && (
                <div style={styles.field}>
                  <label style={styles.label}>Ativada em:</label>
                  <span style={styles.value}>
                    {new Date(opportunity.activated_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
              {opportunity.expires_at && (
                <div style={styles.field}>
                  <label style={styles.label}>Expira em:</label>
                  <span style={styles.value}>
                    {new Date(opportunity.expires_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
              {opportunity.poderoso_expires_at && (
                <div style={styles.field}>
                  <label style={styles.label}>Poderoso expira em:</label>
                  <span style={styles.value}>
                    {new Date(opportunity.poderoso_expires_at).toLocaleString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
          </div>
            </div>
            <aside style={styles.previewColumn}>
              {renderPreview()}
            </aside>
          </div>
        </div>

        <DeactivateModal
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onConfirm={handleDeactivate}
          opportunityName={opportunity.name}
        />

        <UnpublishModal
          isOpen={showUnpublishModal}
          onClose={() => setShowUnpublishModal(false)}
          onConfirm={handleUnpublish}
          opportunityName={opportunity.name}
          recruiters={getUnpublishRecruiters()}
        />

        <RepublishModal
          isOpen={showRepublishModal}
          onClose={() => setShowRepublishModal(false)}
          onConfirm={handleRepublish}
          opportunityName={opportunity.name}
        />

        <ResendAppliesModal
          isOpen={showResendAppliesModal}
          onClose={() => setShowResendAppliesModal(false)}
          onConfirm={handleResendApplies}
          opportunityName={opportunity.name}
          mode={resendAppliesMode}
          limit={resendAppliesMode === 'first_n'
            ? Math.min(Math.max(Number(resendAppliesLimit) || 1, 1), opportunity.applies_count)
            : undefined}
        />
      </div>
    </div>
  );
};

const styles = {
  pageContainer: {
    width: '100%',
  },
  pageShell: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
    minHeight: 'calc(100vh - 5rem)',
  },
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1002,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '95%',
    maxWidth: '1320px',
    maxHeight: '95vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
    position: 'sticky' as const,
    top: 0,
    backgroundColor: colors.white,
    zIndex: 1,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  backBtn: {
    marginBottom: '0.5rem',
    backgroundColor: 'transparent',
    color: colors.primary,
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  detailsLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.45fr) minmax(340px, 0.75fr)',
    gap: '1.5rem',
    alignItems: 'start',
  },
  detailsColumn: {
    minWidth: 0,
  },
  previewColumn: {
    alignSelf: 'start',
  },
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
  },
  sectionTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  label: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: colors.textLight,
  },
  value: {
    fontSize: '1rem',
    color: colors.text,
  },
  badge: {
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    display: 'inline-block',
    width: 'fit-content',
  },
  urlContainer: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  urlLink: {
    flex: 1,
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    backgroundColor: colors.background,
    fontSize: '0.9rem',
    color: colors.primary,
    textDecoration: 'underline',
    display: 'block',
    wordBreak: 'break-all' as const,
  },
  copyBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  recruiterInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  recruiterName: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: colors.text,
  },
  recruiterEmail: {
    fontSize: '0.9rem',
    color: colors.textLight,
  },
  addressList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  addressItem: {
    padding: '0.5rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
  },
  questionsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  questionItem: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.5rem',
    gap: '1rem',
  },
  questionMeta: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  requiredBadge: {
    backgroundColor: colors.error,
    color: colors.white,
    padding: '0.2rem 0.4rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  filterBadge: {
    backgroundColor: '#FFA500',
    color: colors.white,
    padding: '0.2rem 0.4rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  typeBadge: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '0.2rem 0.4rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  answersSection: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: colors.white,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
  },
  answersList: {
    margin: '0.5rem 0 0 0',
    paddingLeft: '1.5rem',
  },
  answerItem: {
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  correctBadge: {
    backgroundColor: colors.success,
    color: colors.white,
    padding: '0.2rem 0.4rem',
    borderRadius: '3px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
  },
  recruitersList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  recruiterItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
  },
  poderosoTag: {
    backgroundColor: '#FFD700',
    color: '#000',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  ownerTag: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  textContent: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
    lineHeight: '1.6',
    whiteSpace: 'pre-wrap' as const,
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '1.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    textAlign: 'center' as const,
  },
  actionEmoji: {
    fontSize: '3rem',
    marginBottom: '1rem',
  },
  actionButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontFamily: 'inherit',
    fontWeight: 'bold',
    marginBottom: '1rem',
    minWidth: '150px',
  },
  actionDescription: {
    margin: 0,
    fontSize: '0.9rem',
    color: colors.textLight,
    lineHeight: '1.4',
  },
  resendAppliesCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    padding: '1.25rem',
    marginBottom: '1.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    flexWrap: 'wrap' as const,
  },
  resendAppliesTitle: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: '0.35rem',
  },
  resendAppliesControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
  },
  resendSelect: {
    padding: '0.65rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    backgroundColor: colors.white,
    color: colors.text,
    fontFamily: 'inherit',
  },
  resendNumberInput: {
    width: '92px',
    padding: '0.65rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    color: colors.text,
    fontFamily: 'inherit',
  },
  resendButton: {
    marginBottom: 0,
    minWidth: '120px',
  },
  statusCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '2rem',
    border: `2px solid ${colors.border}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    textAlign: 'center' as const,
  },
  statusEmoji: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  statusTitle: {
    margin: '0 0 1rem 0',
    color: colors.primary,
    fontSize: '1.3rem',
    fontWeight: 'bold',
  },
  statusInfo: {
    marginBottom: '1rem',
    textAlign: 'left' as const,
    width: '100%',
  },
  statusDescription: {
    margin: 0,
    fontSize: '0.9rem',
    color: colors.textLight,
    lineHeight: '1.4',
    fontStyle: 'italic',
  },
  previewCard: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
    minWidth: 0,
    overflow: 'hidden',
  },
  previewHeader: {
    borderBottom: `1px solid ${colors.border}`,
    paddingBottom: '1rem',
    marginBottom: '1rem',
  },
  previewEyebrow: {
    display: 'block',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    fontSize: '0.75rem',
    color: colors.textLight,
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  previewTitle: {
    margin: '0 0 0.75rem 0',
    color: colors.primary,
    fontSize: '1.45rem',
    lineHeight: 1.2,
    overflowWrap: 'anywhere' as const,
  },
  previewBadges: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  previewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.6rem',
    borderRadius: '999px',
    backgroundColor: colors.primary,
    color: colors.white,
    fontSize: '0.75rem',
    fontWeight: 'bold',
  },
  previewMetaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  previewMetaItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.2rem',
    padding: '0.75rem',
    backgroundColor: colors.background,
    borderRadius: '8px',
    fontSize: '0.85rem',
    minWidth: 0,
    overflowWrap: 'anywhere' as const,
  },
  previewSection: {
    marginBottom: '1.25rem',
  },
  previewSectionTitle: {
    margin: '0 0 0.4rem 0',
    color: colors.primary,
    fontSize: '0.95rem',
    fontWeight: 'bold',
  },
  previewText: {
    color: colors.text,
    fontSize: '0.92rem',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    overflowWrap: 'anywhere' as const,
    wordBreak: 'break-word' as const,
  },
  previewFooter: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.4rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: '0.9rem',
  },
  previewLink: {
    color: colors.primary,
    fontWeight: 'bold',
    textDecoration: 'none',
    marginTop: '0.4rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  error: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.error,
  },
  link: {
    color: colors.primary,
    textDecoration: 'underline',
  },
  noRecruiters: {
    padding: '1rem',
    backgroundColor: colors.background,
    borderRadius: '4px',
    border: `1px solid ${colors.border}`,
    color: colors.textLight,
    fontStyle: 'italic',
    textAlign: 'center' as const,
  },
};

export default OpportunityDetailsModal;
